import { OvertimeLegalReasonEnum, PortalOvertimeCountersFragment } from "@dashboard/graphql";
import { Box, Option, Select, Text } from "@saleor/macaw-ui-next";
import { useIntl } from "react-intl";

import { portalMessages as m } from "./messages";
import { formatMinutes } from "./utils";

const REASONS: Array<[OvertimeLegalReasonEnum, keyof typeof m]> = [
  [OvertimeLegalReasonEnum.TRANSITORY_INCREASE, "reasonTransitory"],
  [OvertimeLegalReasonEnum.FORCE_MAJEURE, "reasonForceMajeure"],
  [OvertimeLegalReasonEnum.SERIOUS_DAMAGE, "reasonSeriousDamage"],
  [OvertimeLegalReasonEnum.INVENTORY, "reasonInventory"],
  [OvertimeLegalReasonEnum.AGREEMENT, "reasonAgreement"],
];

/** The legal ground of overtime (art. 227.º CT, cl. 28.ª CCT). */
export const LegalReasonSelect = ({
  value,
  onChange,
}: {
  value: OvertimeLegalReasonEnum | "";
  onChange: (value: OvertimeLegalReasonEnum) => void;
}) => {
  const intl = useIntl();
  const options: Option[] = REASONS.map(([reason, message]) => ({
    value: reason,
    label: intl.formatMessage(m[message]),
  }));

  return (
    <Select
      size="small"
      label={intl.formatMessage(m.legalReason)}
      options={options}
      value={options.find(option => option.value === value) ?? null}
      onChange={option => onChange(option.value as OvertimeLegalReasonEnum)}
    />
  );
};

/** Day, year and weekly-average counters against the legal limits. */
export const OvertimeCountersView = ({
  counters,
}: {
  counters: PortalOvertimeCountersFragment;
}) => {
  const intl = useIntl();
  const percent = Math.round((counters.yearMinutes * 100) / (counters.yearLimitMinutes || 1));

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {counters.exemption && (
        <Text size={2} color="warning1">
          {intl.formatMessage(m.overtimeExempt, { reason: counters.exemptionLabel })}
        </Text>
      )}
      <Box display="grid" gap={3} __gridTemplateColumns="repeat(auto-fit, minmax(160px, 1fr))">
        <Box display="flex" flexDirection="column">
          <Text size={2} color="default2">
            {intl.formatMessage(m.overtimeToday)}
          </Text>
          <Text size={5} fontWeight="bold">
            {formatMinutes(counters.dayMinutes)} / {formatMinutes(counters.dayLimitMinutes)}
          </Text>
        </Box>
        <Box display="flex" flexDirection="column">
          <Text size={2} color="default2">
            {intl.formatMessage(m.overtimeYear)}
          </Text>
          <Text size={5} fontWeight="bold" color={counters.alert ? "critical1" : undefined}>
            {formatMinutes(counters.yearMinutes)} / {formatMinutes(counters.yearLimitMinutes)} (
            {percent}%)
          </Text>
        </Box>
        <Box display="flex" flexDirection="column">
          <Text size={2} color="default2">
            {intl.formatMessage(m.weeklyAverage)}
          </Text>
          <Text
            size={5}
            fontWeight="bold"
            color={
              counters.weeklyAverageHours > counters.weeklyAverageLimitHours
                ? "critical1"
                : undefined
            }
          >
            {intl.formatNumber(counters.weeklyAverageHours)} h / {counters.weeklyAverageLimitHours}{" "}
            h
          </Text>
        </Box>
      </Box>
      <Text size={1} color="default2">
        {intl.formatMessage(m.overtimePay, { premium: counters.premiumPercent })}
        {counters.compensatoryRestMinutes > 0 &&
          ` · ${intl.formatMessage(m.compensatoryRest, {
            time: formatMinutes(counters.compensatoryRestMinutes),
          })}`}
      </Text>
    </Box>
  );
};
